import React, { useMemo, useCallback } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import type { VisualClusterData } from '@/utils/visualClustering';
import { getClusterColor } from '@/utils/visualClustering';
import { formatPrice } from '@/utils/formatters';

interface VisualClusterMarkerProps {
  cluster: VisualClusterData;
  onClick?: (cluster: VisualClusterData) => void;
  priceRange?: { min: number; max: number }; // Global price range for color scaling
}

/**
 * Create custom marker icon for visual cluster with price-based coloring
 */
function createVisualClusterIcon(
  stationCount: number, 
  cluster: VisualClusterData, 
  priceRange?: { min: number; max: number }
): L.DivIcon {
  // Calculate size based on station count (controlled logarithmic scale)
  const baseSize = 28;
  const maxSize = 50; // Reduced max size for better visual balance
  const size = Math.min(maxSize, baseSize + Math.log(stationCount) * 6); // Reduced multiplier for slower growth
  
  // Use price-based color if available, otherwise fall back to density-based color
  const color = getClusterColor(cluster, priceRange);
  const textColor = '#ffffff'; // Always white text for better contrast

  // Compute a lighter shade for the radial gradient highlight.
  // HSL colors (from getPriceBasedColor) need hsla() conversion — appending 'cc' to an hsl()
  // string produces invalid CSS. Hex colors (#rrggbb) support 8-digit hex opacity fine.
  const lighterColor = color.startsWith('hsl(')
    ? color.replace('hsl(', 'hsla(').replace(')', ', 0.75)')
    : color + 'cc';
  
  // Display count (abbreviated for large numbers)
  const displayCount = stationCount >= 1000 ? `${Math.round(stationCount / 100) / 10}k` : stationCount.toString();
  
  // Display price if available
  const priceDisplay = cluster.average_price_pence ? formatPrice(cluster.average_price_pence) : '';
  
  return L.divIcon({
    html: `
      <div class="visual-cluster-container" style="
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
      ">
        <div class="visual-cluster-marker" style="
          width: ${size}px;
          height: ${size}px;
          background: radial-gradient(circle at 35% 35%, ${lighterColor}, ${color});
          border: 2.5px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 10px ${color}60, 0 0 0 1.5px white;
          font-size: ${Math.max(10, size * 0.3)}px;
          font-weight: bold;
          color: ${textColor};
          transition: all 0.2s ease;
          animation: clusterPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        ">
          ${displayCount}
        </div>
        ${priceDisplay ? `
          <div class="cluster-price-label" style="
            margin-top: 3px;
            padding: 2px 7px;
            background-color: rgba(17,24,39,0.88);
            color: white;
            border-radius: 10px;
            font-size: 10px;
            font-weight: 600;
            white-space: nowrap;
            box-shadow: 0 1px 4px rgba(0,0,0,0.35);
            letter-spacing: 0.01em;
          ">
            ${priceDisplay}/L
          </div>
        ` : ''}
      </div>
      <style>
        .visual-cluster-marker:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        }
        .visual-cluster-container:hover .cluster-price-label {
          background-color: rgba(0,0,0,0.9);
        }
      </style>
    `,
    className: 'visual-cluster-marker-container',
    iconSize: [size, priceDisplay ? size + 20 : size], // Extra height for price label
    iconAnchor: [size / 2, priceDisplay ? (size + 20) / 2 : size / 2],
    popupAnchor: [0, priceDisplay ? -(size + 20) / 2 : -size / 2],
  });
}

export const VisualClusterMarker: React.FC<VisualClusterMarkerProps> = React.memo(({
  cluster,
  onClick,
  priceRange,
}) => {
  const markerIcon = useMemo(() => {
    return createVisualClusterIcon(cluster.station_count, cluster, priceRange);
  }, [cluster.station_count, cluster.average_price_pence, cluster.stations_with_prices, priceRange]);

  const handleClick = useCallback(() => {
    onClick?.(cluster);
  }, [onClick, cluster]);

  // Format brand list for display
  const brandDisplay = useMemo(() => {
    const { brands, dominant_brands, brand_count } = cluster;
    
    if (brand_count <= 3) {
      return brands.join(', ');
    }
    
    const topBrands = dominant_brands.slice(0, 2).join(', ');
    const remainingCount = brand_count - 2;
    return `${topBrands} +${remainingCount} more`;
  }, [cluster.brands, cluster.dominant_brands, cluster.brand_count]);

  return (
    <Marker
      position={[cluster.center.latitude, cluster.center.longitude]}
      icon={markerIcon}
    >
      <Popup autoPan={false}>
        <div className="min-w-[200px] p-3">
          <div className="font-semibold text-gray-900 mb-3 pb-2 border-b border-amber-500/25 flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0"></div>
            <span>{cluster.station_count} Fuel Stations</span>
          </div>
          
          <div className="space-y-2 mb-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Brands:</span>
              <span className="font-medium text-gray-900">
                {cluster.brand_count}
              </span>
            </div>

            {/* Pricing information */}
            {cluster.average_price_pence && (
              <div className="border-t pt-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Average Price:</span>
                  <span className="font-bold text-gray-900">
                    {formatPrice(cluster.average_price_pence)}
                  </span>
                </div>
                {cluster.price_range && (
                  <div className="text-xs text-gray-500 mt-1">
                    Range: {formatPrice(cluster.price_range.min_pence)} - {formatPrice(cluster.price_range.max_pence)}
                  </div>
                )}
                {cluster.stations_with_prices !== undefined && (
                  <div className="text-xs text-gray-500 mt-1">
                    Based on {cluster.stations_with_prices} station{cluster.stations_with_prices !== 1 ? 's' : ''} with pricing data
                  </div>
                )}
              </div>
            )}
            
            <div className="border-t pt-2">
              <div className="text-gray-600 text-xs mb-1">Available brands:</div>
              <div className="text-gray-800 text-xs leading-relaxed">
                {brandDisplay}
              </div>
            </div>

            {cluster.average_price_pence && (
              <div className="border-t pt-2">
                <div className="text-gray-500 text-xs">
                  Color indicates price level — green is cheaper, red is more expensive.
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
            <button
              onClick={handleClick}
              className="btn-primary text-xs px-3 py-1.5"
            >
              Zoom In
            </button>
            <span className="text-xs text-gray-400">to see individual stations</span>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to optimize re-renders
  return (
    prevProps.cluster.id === nextProps.cluster.id &&
    prevProps.cluster.station_count === nextProps.cluster.station_count &&
    prevProps.cluster.average_price_pence === nextProps.cluster.average_price_pence &&
    prevProps.priceRange?.min === nextProps.priceRange?.min &&
    prevProps.priceRange?.max === nextProps.priceRange?.max &&
    prevProps.onClick === nextProps.onClick
  );
});

VisualClusterMarker.displayName = 'VisualClusterMarker';

export default VisualClusterMarker;